import { assertNever, panic } from "@glideapps/ts-necessities";
import { arrayMapSync, mapMap } from "collection-utils";
import type { PrimitiveValue } from "../common/components/computation-types";
import { geocodeAddress } from "../common/components/geocode";
import type { MinimalAppEnvironment } from "../common/components/types";
import type { GroundValue, LoadedGroundValue, LoadingValue } from "../common/computation-model/data";
import {
    asMaybeDate,
    asMaybeNumber,
    asMaybeString,
    asPrimitive,
    getRowColumn,
    isLoadingValue,
    isPrimitive,
    isRow,
} from "../common/computation-model/data";
import type { Path } from "../common/computation-model/paths";
import { getSymbolicRepresentationForPath } from "../common/computation-model/paths";
import type { AsyncComputation, RootPathResolver } from "../common/computation-model/types";
import type { LatLng } from "../common/Database";
import { formatDateTime } from "../common/format-date-time";
import { getDistanceFromLatLonInKm, milesPerKM } from "../common/geometry";
import { isArray } from "../support";
import { parseUserDateTimeZoneAgnostic } from "../support/parse-user-date-time";
import type { FormatDateTimeSpecification } from "../generator/formulas/formula-specifications";
import { DateTimeParts, DistanceUnit } from "../generator/formulas/formula-specifications";
import { getValueAt } from "./handlers";

export class GeoDistanceAsyncComputation implements AsyncComputation {
    constructor(
        private readonly _firstLocationPath: Path,
        private readonly _secondLocationPath: Path,
        private readonly _unit: DistanceUnit,
        private readonly _appEnvironment: MinimalAppEnvironment,
        private readonly _quotaKey: string
    ) {}

    public getPaths(): readonly Path[] {
        return [this._firstLocationPath, this._secondLocationPath];
    }

    private lookupLocation(value: LoadedGroundValue): Promise<LatLng | undefined> | LoadingValue | LatLng | undefined {
        if (isRow(value)) {
            const latValue = getRowColumn(value, "latitude");
            if (isLoadingValue(latValue)) return latValue;
            const lat = asMaybeNumber(latValue);
            const lngValue = getRowColumn(value, "longitude");
            if (isLoadingValue(lngValue)) return lngValue;
            const lng = asMaybeNumber(lngValue);
            if (lat === undefined || lng === undefined) return undefined;
            return { lat, lng };
        }

        const str = asMaybeString(value);
        if (str === undefined) return undefined;

        return geocodeAddress(this._appEnvironment, str, this._quotaKey);
    }

    private computeDistance(firstLocation: LatLng | undefined, secondLocation: LatLng | undefined): number | undefined {
        if (firstLocation === undefined || secondLocation === undefined) return undefined;

        const km = getDistanceFromLatLonInKm(
            firstLocation.lat,
            firstLocation.lng,
            secondLocation.lat,
            secondLocation.lng
        );
        switch (this._unit) {
            case DistanceUnit.KM:
                return km;
            case DistanceUnit.Miles:
                return km * milesPerKM;
            default:
                return assertNever(this._unit);
        }
    }

    private async computeFromPromises(
        firstPromise: Promise<LatLng | undefined> | LatLng | undefined,
        secondPromise: Promise<LatLng | undefined> | LatLng | undefined
    ): Promise<number | undefined> {
        const [firstLocation, secondLocation] = await Promise.all([firstPromise, secondPromise]);
        return this.computeDistance(firstLocation, secondLocation);
    }

    public compute(resolver: RootPathResolver, context: GroundValue): Promise<GroundValue> | GroundValue {
        const firstValue = getValueAt(resolver, context, this._firstLocationPath);
        if (isLoadingValue(firstValue)) return firstValue;
        const secondValue = getValueAt(resolver, context, this._secondLocationPath);
        if (isLoadingValue(secondValue)) return secondValue;

        const maybeFirstLocation = this.lookupLocation(firstValue);
        if (isLoadingValue(maybeFirstLocation)) return maybeFirstLocation;
        const maybeSecondLocation = this.lookupLocation(secondValue);
        if (isLoadingValue(maybeSecondLocation)) return maybeSecondLocation;

        if (maybeFirstLocation instanceof Promise || maybeSecondLocation instanceof Promise) {
            return this.computeFromPromises(maybeFirstLocation, maybeSecondLocation);
        } else {
            return this.computeDistance(maybeFirstLocation, maybeSecondLocation);
        }
    }

    public get symbolicRepresentation(): string {
        return `(geo-distance from: ${getSymbolicRepresentationForPath(
            this._firstLocationPath
        )} to: ${getSymbolicRepresentationForPath(this._secondLocationPath)})`;
    }
}

export class FormatDateTimeComputation implements AsyncComputation {
    constructor(private readonly _dateTimePath: Path, private readonly _spec: FormatDateTimeSpecification) {}

    public getPaths(): readonly Path[] {
        return [this._dateTimePath];
    }

    private format(d: Date): string | undefined {
        const { parts, dateFormat, timeFormat } = this._spec;
        switch (parts) {
            case DateTimeParts.DateOnly:
                return formatDateTime(d, dateFormat, undefined);
            case DateTimeParts.TimeOnly:
                return formatDateTime(d, undefined, timeFormat);
            case DateTimeParts.DateTime:
                return formatDateTime(d, dateFormat, timeFormat);
            default:
                return assertNever(parts);
        }
    }

    private async awaitAndFormat(p: Promise<Date | undefined>): Promise<PrimitiveValue> {
        const d = await p;
        if (d === undefined) return undefined;
        return this.format(d);
    }

    private formatPrimitive(p: PrimitiveValue): Promise<PrimitiveValue> | PrimitiveValue {
        if (typeof p === "string") {
            const maybeDate = parseUserDateTimeZoneAgnostic(p);
            if (maybeDate instanceof Promise) {
                return this.awaitAndFormat(maybeDate);
            } else if (maybeDate === undefined) {
                return undefined;
            } else {
                return this.format(maybeDate);
            }
        } else {
            const d = asMaybeDate(p);
            if (d === undefined) return undefined;
            return this.format(d);
        }
    }

    public compute(ns: RootPathResolver, context: GroundValue): Promise<GroundValue> | GroundValue {
        const p = getValueAt(ns, context, this._dateTimePath);
        if (isLoadingValue(p)) {
            return p;
        } else if (isPrimitive(p)) {
            return this.formatPrimitive(p);
        } else if (isArray(p)) {
            const a = p.map(v => this.formatPrimitive(asPrimitive(v)));
            if (a.some(v => v instanceof Promise)) {
                return arrayMapSync(a, async v => await v);
            } else {
                return a.map(v => {
                    if (v instanceof Promise) {
                        return panic("We already asserted this above");
                    }
                    return v;
                });
            }
        } else {
            return undefined;
        }
    }

    public get symbolicRepresentation(): string {
        return `(format-date-time ${getSymbolicRepresentationForPath(this._dateTimePath)})`;
    }
}

export class YesCodeComputation implements AsyncComputation {
    constructor(
        private readonly _urlPath: Path,
        private readonly _paramPaths: ReadonlyMap<string, Path>,
        private readonly _appEnvironment: MinimalAppEnvironment
    ) {}

    public getPaths(): readonly Path[] {
        return [this._urlPath, ...this._paramPaths.values()];
    }

    public compute(resolver: RootPathResolver, context: GroundValue): GroundValue | Promise<GroundValue> {
        const url = getValueAt(resolver, context, this._urlPath);
        if (typeof url !== "string") return undefined;

        const params = mapMap(this._paramPaths, p => getValueAt(resolver, context, p));
        return this._appEnvironment.callYesCode?.(url, params);
    }

    public get symbolicRepresentation(): string {
        return `(yes-code ${getSymbolicRepresentationForPath(this._urlPath)} ${Array.from(this._paramPaths)
            .map(([k, p]) => `${k}: ${getSymbolicRepresentationForPath(p)}`)
            .join(" ")})`;
    }
}
